import React, { useState, useEffect } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@components/ui/alert';
import { Button } from '@components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@components/ui/dialog';
import { Checkbox } from '@components/checkbox';
import { TextField } from '@components/textfield';
import { TenantCorporateContext } from '.';
import { ApprovedMoneyTopUpDto } from '@api/CorporateClient';

interface ChallengePayoutModalProps {
    open?: boolean;
    onModalClose?(): void;
    tenantName: string;
}

export const ChallengePayoutModal = ({
    open: initialOpen = false,
    onModalClose,
    tenantName
}: ChallengePayoutModalProps) => {
    const [open, setOpen] = useState(initialOpen);
    const corporateClient = React.useContext(TenantCorporateContext);

    const [challengeId, setChallengeId] = useState('');
    const [moneyValue, setMoneyValue] = useState('');
    const [currencyCode, setCurrencyCode] = useState('STR');
    const [userEmails, setUserEmails] = useState('');
    const [userIds, setUserIds] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [consentChecked, setConsentChecked] = useState(false);

    const handleClose = () => {
        setOpen(false);
        onModalClose?.();
        setChallengeId('');
        setMoneyValue('');
        setCurrencyCode('');
        setUserEmails('');
        setUserIds('');
        setError(null);
        setSuccess(false);
        setConsentChecked(false);
    };

    useEffect(() => {
        setOpen(initialOpen);
    }, [initialOpen]);

    const handleSubmit = async () => {
        setError(null);
        setSuccess(false);

        if (!challengeId || !moneyValue || !currencyCode) {
            setError(
                'Challenge ID, Money Value, and Currency Code are required'
            );
            return;
        }

        if (!userEmails && !userIds) {
            setError(
                'At least one of User Emails or User IDs must be provided'
            );
            return;
        }

        try {
            setLoading(true);

            const payload: ApprovedMoneyTopUpDto = {
                tenantId: tenantName,
                entityId: parseInt(challengeId, 10),
                entityType: 'CHALLENGE',
                moneyValue: parseFloat(moneyValue),
                currencyCode: currencyCode.trim(),
                ...(userEmails && {
                    userEmails: userEmails
                        .split(',')
                        .map((email) => email.trim())
                        .filter((email) => email.length > 0)
                }),
                ...(userIds && {
                    userIds: userIds
                        .split(',')
                        .map((id) => id.trim())
                        .filter((id) => id.length > 0)
                })
            };

            await corporateClient.support.walletSupportControllerTopUpWalletAccounts(
                payload
            );

            setSuccess(true);

            setTimeout(() => {
                handleClose();
            }, 2000);
        } catch (err: any) {
            setError(err?.message || 'Failed to process payout');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(next) => !next && handleClose()}>
            <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle>Challenge Payout</DialogTitle>
                    <DialogDescription>
                        Top up wallet accounts for users in a challenge.
                    </DialogDescription>
                </DialogHeader>
                {error && (
                    <Alert variant="destructive">
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}
                {success && (
                    <Alert variant="success">
                        <AlertDescription>
                            Payout processed successfully!
                        </AlertDescription>
                    </Alert>
                )}

                <div className="flex flex-col gap-4">
                    <TextField
                        label="Challenge ID (from Management Portal browser url)"
                        type="number"
                        required
                        value={challengeId}
                        onChange={(value) => setChallengeId(String(value))}
                        disabled={loading}
                    />

                    <TextField
                        label="Money Value (per person)"
                        type="number"
                        required
                        value={moneyValue}
                        onChange={(value) => setMoneyValue(String(value))}
                        disabled={loading}
                    />

                    <TextField
                        label="Currency Code (STR = Stars)"
                        type="text"
                        required
                        value={currencyCode}
                        onChange={(value) => setCurrencyCode(String(value))}
                        disabled={true}
                    />

                    <div className="flex flex-col gap-4 rounded-lg border p-4">
                        <p className="text-sm font-semibold">Recipients</p>

                        <TextField
                            label="User Emails (comma-separated)"
                            type="text"
                            value={userEmails}
                            onChange={(value) => setUserEmails(String(value))}
                            disabled={loading}
                            placeholder="email1@example.com, email2@example.com"
                            multiline
                            maxRows={2}
                        />

                        <span className="text-xs text-muted-foreground">
                            or
                        </span>

                        <TextField
                            label="User IDs (comma-separated)"
                            type="text"
                            value={userIds}
                            onChange={(value) => setUserIds(String(value))}
                            disabled={loading}
                            placeholder="123, 456, 789"
                            multiline
                            maxRows={2}
                        />

                        <span className="text-xs text-muted-foreground">
                            * At least one of User Emails or User IDs must be
                            provided
                        </span>
                    </div>

                    <Alert variant="warning">
                        <AlertTitle>Warning: Financial Transaction</AlertTitle>
                        <AlertDescription>
                            This action will send real money to the specified
                            users. Please ensure all details are correct before
                            proceeding.
                        </AlertDescription>
                    </Alert>

                    <Checkbox
                        checked={consentChecked}
                        disabled={loading}
                        center
                        onChange={(_, checked) => setConsentChecked(checked)}
                    >
                        I understand that this action will send money and accept
                        the consequences
                    </Checkbox>
                </div>
                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={handleClose}
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        loading={loading}
                        disabled={!consentChecked}
                    >
                        Submit Payout
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
